/**
 * The tenant permissions of AI over MCP, in the backend's words: `users/client_mcp_permissions.py` (client-MCP plan
 * 2026-10-20 §4.9, §5.6, §6.4, §10.1, §12.5, §13.2, Q26; users 0150) plus `notification.mcp_read` / `_write`
 * (users 0145). Every one is an **explicit grant**: Full access does not imply it (backend `ag_has_explicit_perm`),
 * so a page gates on `hasExplicitPerm`, never on `hasPerm`.
 *
 * The permission page shows them, grouped apart from a scope's regular permissions (`AccessGroupEditPermissions`);
 * the approvals inbox names the approve permission a waiting change needs (`aiChanges/parts.js`).
 * `mcpPermissions.test.js` holds this list to the backend's codenames.
 */

/** codename -> the backend's name ("<Scope>: <what>"). */
export const MCP_PERMISSIONS = {
    // Content types (§4.9) and their approve permissions (§10.1).
    'tour.mcp_list': 'Tour: AI over MCP — list',
    'tour.mcp_read': 'Tour: AI over MCP — read',
    'tour.mcp_write': 'Tour: AI over MCP — propose, apply, revert',
    'tour.mcp_approve': 'Tour: AI over MCP — approve pending changes',
    'itinerary.mcp_list': 'Itinerary: AI over MCP — list',
    'itinerary.mcp_read': 'Itinerary: AI over MCP — read',
    'itinerary.mcp_write': 'Itinerary: AI over MCP — propose, apply, revert',
    'itinerary.mcp_approve': 'Itinerary: AI over MCP — approve pending changes',
    'activity.mcp_list': 'Activity: AI over MCP — list',
    'activity.mcp_read': 'Activity: AI over MCP — read',
    'activity.mcp_write': 'Activity: AI over MCP — propose, apply, revert',
    'activity.mcp_approve': 'Activity: AI over MCP — approve pending changes',
    'region.mcp_list': 'Region: AI over MCP — list',
    'region.mcp_read': 'Region: AI over MCP — read',
    'region.mcp_write': 'Region: AI over MCP — propose, apply, revert',
    'region.mcp_approve': 'Region: AI over MCP — approve pending changes',
    'trip_type.mcp_list': 'Trip type: AI over MCP — list',
    'trip_type.mcp_read': 'Trip type: AI over MCP — read',
    'trip_type.mcp_write': 'Trip type: AI over MCP — propose, apply, revert',
    'trip_type.mcp_approve': 'Trip type: AI over MCP — approve pending changes',
    'vehicle_fleet.mcp_list': 'Vehicle fleet: AI over MCP — list',
    'vehicle_fleet.mcp_read': 'Vehicle fleet: AI over MCP — read',
    'vehicle_fleet.mcp_write': 'Vehicle fleet: AI over MCP — propose, apply, revert',
    'vehicle_fleet.mcp_approve': 'Vehicle fleet: AI over MCP — approve pending changes',
    'activity_category.mcp_list': 'Activity category: AI over MCP — list',
    'activity_category.mcp_read': 'Activity category: AI over MCP — read',
    'activity_category.mcp_write': 'Activity category: AI over MCP — propose, apply, revert',
    'activity_category.mcp_approve': 'Activity category: AI over MCP — approve pending changes',
    'pre_trip_item.mcp_list': 'Pre-trip item: AI over MCP — list',
    'pre_trip_item.mcp_read': 'Pre-trip item: AI over MCP — read',
    'pre_trip_item.mcp_write': 'Pre-trip item: AI over MCP — propose, apply, revert',
    'pre_trip_item.mcp_approve': 'Pre-trip item: AI over MCP — approve pending changes',
    'tag.mcp_list': 'Tour tag: AI over MCP — list',
    'tag.mcp_read': 'Tour tag: AI over MCP — read',
    'tag.mcp_write': 'Tour tag: AI over MCP — propose, apply, revert',
    'tag.mcp_approve': 'Tour tag: AI over MCP — approve pending changes',
    // Notification content (users 0145) and the client-MCP additions (§4.9, §10.1).
    'notification.mcp_list': 'Notification: AI editing over MCP — list',
    'notification.mcp_read': 'Notification: AI editing over MCP — read',
    'notification.mcp_write': 'Notification: AI editing over MCP — propose, apply, revert',
    'notification.mcp_approve': 'Notification: AI editing over MCP — approve pending changes',
    // Issue triage (§5.6).
    'triage.mcp_use': 'Triage: AI over MCP — run checks, read case files',
    'triage.mcp_escalate': 'Triage: AI over MCP — escalate to support',
    'triage.mcp_operate': 'Triage: AI over MCP — operate (resync bookings)',
    'triage.mcp_diagnose': 'Triage: AI over MCP — LLM diagnose',
    // Guest & staff analytics (§6.4).
    'analytics.mcp_read': 'Analytics: AI over MCP — read aggregates',
    'analytics.mcp_export': 'Analytics: AI over MCP — export guest lists (personal data)',
    // Tenant vocabulary (§12.5).
    'vocabulary.mcp_write': 'AI vocabulary: AI over MCP — propose, apply, revert',
    'vocabulary.mcp_approve': 'AI vocabulary: AI over MCP — approve pending changes',
    // The tenant's AI editing settings: auto-apply (Q26) and the guest-data opt-in.
    'ai_edit.manage': 'AI editing: manage settings (auto-apply, guest-data opt-in)',
    // Integration developers on production (§13.2).
    'import.mcp_list': 'Import: AI over MCP — list',
    'import.mcp_read': 'Import: AI over MCP — read',
    'import.mcp_write': 'Import: AI over MCP — import files, redeliver, send test events',
    'tour_participant.mcp_list': 'Tour participant: AI over MCP — list',
    'tour_participant.mcp_read': 'Tour participant: AI over MCP — read',
};

const codename = (scope, action) => `${scope}.${action}`;

/** Is `scope.action` one of the explicit-only AI-over-MCP permissions? */
export const isMcpPermission = (scope, action) => Object.prototype.hasOwnProperty.call(MCP_PERMISSIONS, codename(scope, action));

/** The permission's name ("Tour: AI over MCP — list"), or null for any other permission. */
export const mcpPermissionName = (scope, action) => (isMcpPermission(scope, action) ? MCP_PERMISSIONS[codename(scope, action)] : null);

/** Its name without the scope ("AI over MCP — list"): the permission page shows it under the scope's heading. */
export const mcpActionLabel = (scope, action) => {
    const name = mcpPermissionName(scope, action);
    if (!name) return null;
    const at = name.indexOf(': ');
    return at >= 0 ? name.slice(at + 2) : name;
};

/** What a pending change's `scopes` touch, as the approvals inbox names them (an unknown scope reads as itself). */
const SCOPE_NOUNS = {
    notification: 'Notifications',
    tour: 'Tours',
    itinerary: 'Itineraries',
    activity: 'Activities',
    region: 'Regions',
    trip_type: 'Trip types',
    vehicle_fleet: 'Vehicle fleet',
    activity_category: 'Activity categories',
    pre_trip_item: 'Trip items',
    tag: 'Tags',
    vocabulary: 'AI vocabulary',
};

export const scopeNoun = (scope) => SCOPE_NOUNS[scope] || String(scope || '').replace(/_/g, ' ');

/** The approve permission of a scope, by name ("Tour: AI over MCP — approve pending changes"). */
export const approvePermissionName = (scope) => mcpPermissionName(scope, 'mcp_approve') || `${scope}.mcp_approve`;
